// src/store/categories.store.ts

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { observable, action, makeObservable, runInAction } from 'mobx';

// API
import api from 'api';

// Types
import {
  IProps,
  ICategory,
  ICategoryItem,
  IPaginationResponse,
  ListType,
  IPartialCategoryItem
} from 'types/types';
import axios from 'axios';
import type { SlotName } from 'api/categories';

const emptyCategory = (): ICategoryItem => ({
  id: 0,
  sort: 0,
  name: '',
  alternative_name: null,
  seo_description_template: null,
  seo_title_template: null,
  description_template: null,
  parent_id: null,
  seo_description: null,
  seo_title: null,
  slug: null,
  text_content: null,
  created_at: new Date(),
  updated_at: new Date(),
  children: [],
  containers: [],
  best_variations: [],
  applicable_characteristic_types: [],
  applicable_characteristic_types_for_filter: [],
  applicable_characteristic_types_for_list: [],
  applicable_tags: []
});

interface IError {
  isOk: boolean;
  msg: string;
}

interface IAddCategory {
  name: string;
  parent_id?: number;
}

class CategoriesStore {
  @observable
  categories: ListType<ICategory> | IPaginationResponse<ICategory> = [];

  @observable
  childrenCategories: ListType<ICategory> | IPaginationResponse<ICategory> = [];

  @observable
  category: ICategoryItem = emptyCategory();

  @observable
  loading: boolean = true;

  @observable
  slotLoading: Record<string, boolean> = {};

  @observable
  slotError: string | null = null;

  constructor() {
    makeObservable(this);
  }

  private static keyFor(id: number, slot: SlotName): string {
    return `${String(id)}:${slot}`;
  }

  /** локально установить URL слота, если соответствующее поле существует */
  private static setSlotUrlLocal(
    cat: ICategoryItem,
    slot: SlotName,
    url: string | null
  ): void {
    const anyCat = cat as unknown as Record<string, unknown>;
    let field = '';

    if (slot === 'icon') field = 'icon_url';
    if (slot === 'extra1') field = 'extra1_url';
    if (slot === 'extra2') field = 'extra2_url';

    if (field.length > 0) {
      const hasField = Object.prototype.hasOwnProperty.call(anyCat, field);
      if (hasField) {
        anyCat[field] = url;
      }
    }
  }

  @action
  getCategories = async ({
    page,
    paginate,
    perpage
  }: IProps): Promise<IError> => {
    this.loading = true;

    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      const categoriesFromApi = await api.categories.getCategoriesList(
        page,
        paginate,
        perpage
      );

      runInAction(() => {
        if (categoriesFromApi.status === 200) {
          this.categories = categoriesFromApi.data;

          res.isOk = true;
          res.msg = 'Категории получены';
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error\nCode: ${categoriesFromApi.status}\nStatus: ${categoriesFromApi.statusText}`
          );
          res.isOk = false;
          res.msg = categoriesFromApi.statusText;
          this.categories = [];
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${error.response?.data.message}`;
      }

      this.loading = false;
      res.isOk = false;

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  clearAll = (): void => {
    this.loading = true;
    this.categories = [];
    this.category = emptyCategory();
  };

  @action
  getCategory = async (id: number): Promise<IError> => {
    this.loading = true;

    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      const categoryFromApi = await api.categories.getCategory(id);

      runInAction(() => {
        if (categoryFromApi.status === 200) {
          this.category = categoryFromApi.data;

          res.isOk = true;
          res.msg = 'Категория получена';
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error\nCode: ${categoryFromApi.status}\nStatus: ${categoryFromApi.statusText}`
          );
          this.category = emptyCategory();
          res.isOk = false;
          res.msg = categoryFromApi.statusText || '';
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${error.response?.data.message}`;
      }

      this.loading = false;
      res.isOk = false;

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  addCategory = async (params: IAddCategory): Promise<IError> => {
    this.loading = true;

    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      const categoryFromApi = await api.categories.addCategory(params);

      runInAction(() => {
        if (categoryFromApi.status === 201) {
          res.isOk = true;
          res.msg = 'Категория добавлена';
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error\nCode: ${categoryFromApi.status}\nStatus: ${categoryFromApi.statusText}`
          );

          res.isOk = false;
          if (categoryFromApi.name) {
            res.msg = `Ошибка ${categoryFromApi.name}: ${categoryFromApi.message}`;
          } else {
            res.msg = `Ошибка ${categoryFromApi.status}: ${categoryFromApi.statusText}`;
          }
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${
          error.response?.data.message || error.name
        }`;
      }

      this.loading = false;
      res.isOk = false;

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  changePositions = async (
    id1: number,
    id2: number,
    category1: ICategory,
    category2: ICategory
  ): Promise<IError> => {
    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      await api.categories
        .editCategory(id1, {
          ...category1
        })
        .then(async () => {
          await api.categories
            .editCategory(id2, {
              ...category2
            })
            .then(() => {
              res.isOk = true;
              res.msg = 'Порядок изменён';
            })
            .catch((error) => {
              if (axios.isAxiosError(error)) {
                res.msg = `${error.message}:\n ${
                  error.response?.data.message || error.name
                }`;
              }
              res.isOk = false;

              // eslint-disable-next-line no-console
              console.error(error);
            });
        })
        .catch((error) => {
          if (axios.isAxiosError(error)) {
            res.msg = `${error.message}:\n ${
              error.response?.data.message || error.name
            }`;
          }

          this.loading = false;
          res.isOk = false;

          // eslint-disable-next-line no-console
          console.error(error);
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  editCategory = async (id: number, category: ICategory): Promise<IError> => {
    this.loading = true;

    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      const productFromApi = await api.categories.editCategory(id, category);

      runInAction(() => {
        if (productFromApi.status === 200) {
          res.isOk = true;
          res.msg = 'Категория изменена';
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error\nCode: ${productFromApi.status}\nStatus: ${productFromApi.statusText}`
          );

          res.isOk = false;
          if (productFromApi.name) {
            res.msg = `Ошибка ${productFromApi.name}: ${productFromApi.message}`;
          } else {
            res.msg = `Ошибка ${productFromApi.status}: ${productFromApi.statusText}`;
          }
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${
          error.response?.data.message || error.name
        }`;
      }

      this.loading = false;
      res.isOk = false;

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  updateCategory = async (
    id: number,
    params: IPartialCategoryItem
  ): Promise<IError> => {
    this.loading = true;

    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      const categoryFromApi = await api.categories.patchCategory(id, params);

      runInAction(() => {
        if (categoryFromApi.status === 200) {
          res.isOk = true;
          res.msg = 'Категория изменена';
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error\nCode: ${categoryFromApi.status}\nStatus: ${categoryFromApi.statusText}`
          );

          res.isOk = false;
          if (categoryFromApi.name) {
            res.msg = `Ошибка ${categoryFromApi.name}: ${categoryFromApi.message}`;
          } else {
            res.msg = `Ошибка ${categoryFromApi.status}: ${categoryFromApi.statusText}`;
          }
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${
          error.response?.data.message || error.name
        }`;
      }

      this.loading = false;
      res.isOk = false;

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  @action
  deleteCategory = async (id: number): Promise<IError> => {
    this.loading = true;

    const res: IError = {
      isOk: false,
      msg: ''
    };

    try {
      const categoryFromApi = await api.categories.deleteCategory(id);

      runInAction(() => {
        if (categoryFromApi.status === 200) {
          res.isOk = true;
          res.msg = 'Категория удалена';
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error\nCode: ${categoryFromApi.status}\nStatus: ${categoryFromApi.statusText}`
          );

          res.isOk = false;
          if (categoryFromApi.name) {
            res.msg = `Ошибка ${categoryFromApi.name}: ${categoryFromApi.message}`;
          } else {
            res.msg = `Ошибка ${categoryFromApi.status}: ${categoryFromApi.statusText}`;
          }
        }
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        res.msg = `${error.message}:\n ${
          error.response?.data.message || error.name
        }`;
      }

      this.loading = false;
      res.isOk = false;

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }

    return res;
  };

  /** Удалить файл из слота с оптимистичным UI (ставим заглушку сразу) */
  @action
  removeSlot = async (id: number, slot: SlotName): Promise<IError> => {
    const res: IError = { isOk: false, msg: '' };
    const key = CategoriesStore.keyFor(id, slot);
    this.slotError = null;
    this.slotLoading[key] = true;

    const current = this.category;
    const snapshot: {
      icon_url?: unknown;
      extra1_url?: unknown;
      extra2_url?: unknown;
    } = {};

    snapshot.icon_url = (
      current as unknown as Record<string, unknown>
    ).icon_url;
    snapshot.extra1_url = (
      current as unknown as Record<string, unknown>
    ).extra1_url;
    snapshot.extra2_url = (
      current as unknown as Record<string, unknown>
    ).extra2_url;

    // оптимистично убираем превью
    runInAction(() => {
      CategoriesStore.setSlotUrlLocal(current, slot, null);
    });

    try {
      // предпочитаем safe-вариант, если он есть
      const hasSafe =
        typeof (api.categories as unknown as Record<string, unknown>)
          .removeSlotSafe === 'function';

      if (hasSafe) {
        const safe = await (
          api.categories as unknown as {
            removeSlotSafe: (
              cid: number,
              s: SlotName
            ) => Promise<{ ok: boolean; message?: string }>;
          }
        ).removeSlotSafe(id, slot);

        if (!safe.ok) {
          runInAction(() => {
            (current as unknown as Record<string, unknown>).icon_url =
              snapshot.icon_url;
            (current as unknown as Record<string, unknown>).extra1_url =
              snapshot.extra1_url;
            (current as unknown as Record<string, unknown>).extra2_url =
              snapshot.extra2_url;
            this.slotError = safe.message || 'Не удалось удалить файл';
          });
          res.isOk = false;
          if (safe.message) res.msg = safe.message;
          else res.msg = 'Не удалось удалить файл';
          return res;
        }
      } else {
        await api.categories.removeSlot(id, slot);
      }

      runInAction(() => {
        res.isOk = true;
        res.msg = 'Файл удалён';
      });
    } catch (error) {
      let msg = 'Не удалось удалить файл';
      if (axios.isAxiosError(error)) {
        const serverMsg =
          error.response?.data &&
          (error.response.data as Record<string, unknown>).message;
        if (typeof serverMsg === 'string' && serverMsg.length > 0) {
          msg = serverMsg;
        } else {
          msg = error.message;
        }
      }

      runInAction(() => {
        // откат
        (current as unknown as Record<string, unknown>).icon_url =
          snapshot.icon_url;
        (current as unknown as Record<string, unknown>).extra1_url =
          snapshot.extra1_url;
        (current as unknown as Record<string, unknown>).extra2_url =
          snapshot.extra2_url;
        this.slotError = msg;
        res.isOk = false;
        res.msg = msg;
      });
    } finally {
      runInAction(() => {
        delete this.slotLoading[key];
      });
    }

    return res;
  };

  /** Загрузить файл в слот; если бэк вернул URL — обновляем локально */
  @action
  uploadSlot = async (
    id: number,
    slot: SlotName,
    file: File,
    opts?: { signal?: AbortSignal; onProgress?: (e: ProgressEvent) => void }
  ): Promise<IError> => {
    const res: IError = { isOk: false, msg: '' };
    const key = CategoriesStore.keyFor(id, slot);
    this.slotError = null;
    this.slotLoading[key] = true;

    try {
      const resp = await api.categories.uploadSlot(id, slot, file, {
        signal: opts && opts.signal ? opts.signal : undefined,
        onUploadProgress: opts && opts.onProgress ? opts.onProgress : undefined
      });

      const newUrl = resp && resp.data && resp.data.url ? resp.data.url : '';
      if (typeof newUrl === 'string' && newUrl.length > 0) {
        runInAction(() => {
          CategoriesStore.setSlotUrlLocal(this.category, slot, newUrl);
        });
      }

      runInAction(() => {
        res.isOk = true;
        res.msg = 'Файл загружен';
      });
    } catch (error) {
      let msg = 'Не удалось загрузить файл';
      if (axios.isAxiosError(error)) {
        const serverMsg =
          error.response?.data &&
          (error.response.data as Record<string, unknown>).message;
        if (typeof serverMsg === 'string' && serverMsg.length > 0) {
          msg = serverMsg;
        } else {
          msg = error.message;
        }
      }

      runInAction(() => {
        this.slotError = msg;
        res.isOk = false;
        res.msg = msg;
      });
    } finally {
      runInAction(() => {
        delete this.slotLoading[key];
      });
    }

    return res;
  };
}

const categoriesStore = new CategoriesStore();
export default categoriesStore;
